import BaseObject from '~/App/shared/interfaces/BaseObject';
import { GearboxType } from '../../types/GearboxType';
import { SalesFlowType } from '../../types/SalesFlowType';
import { TicketRole } from '../../types/TicketRole';
import { FuelCodeType } from '../Fuel';
import { SelectOption } from '../SelectOption';
import { SpecItem, TechnicalSpecification } from '../TechnicalSpecification';
import { Nullable } from '../../types/Nullable';
import { Facility } from '~/App/shared/interfaces/Facility';
import { TimeSlot } from '../../types/TimeSlot';
import { CreateProposalInput } from '../../actions/sellFlowAsyncActions';
import { SellFlowFormHeaderRecord } from '~/config/generated/graphql';

export interface Interval {
  currency: string;
  max: number;
  min: number;
  value?: number;
  recommendation?: number;
  provision?: number;
}

type AddonItem = SpecItem & { value: boolean };

interface SellFlowBaseObject extends BaseObject {
  includedAddons?: {
    addons?: AddonItem[];
    package?: AddonItem;
    valuationStandardAddons?: AddonItem[];
  };
}

export enum SalesMethodTypes {
  BIDDING = 'BIDDING',
  FIXED_PRICE = 'FIXED_PRICE'
}

export enum SalesAgreementProvider {
  COMFACT = 'COMFACT',
  BANK_ID = 'BANK_ID',
  SCRIVE = 'SCRIVE'
}

export enum SigningInstanceStatus {
  Active = 'Active',
  Approved = 'Approved',
  Completed = 'Completed',
  Created = 'Created',
  Declined = 'Declined',
  Expired = 'Expired',
  Halted = 'Halted',
  Withdrawn = 'Withdrawn'
}

export interface BusinessProposal {
  denial?: {
    denialReason: string;
  };
  baseObject: SellFlowBaseObject;
  isSigningContractRequired?: boolean;
  baseObjectId: string;
  clientHasMemberAccount: boolean;
  clientMemberEmail: string;
  clientMemberId: string;
  auctionPriceInterval?: Interval;
  tradeInPriceInterval?: Interval;
  reservationPriceInterval?: Interval;
  salesFlow: SalesFlowType;
  deniedByKvd: boolean;
  expirationDate: string;
  hasDebt: boolean;
  hasManualValuation: boolean;
  isExpired: boolean;
  requiresManualValuation?: boolean;
  manualValuationPending: boolean;
  isTradeIn?: boolean;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  salesAgreementSigned: boolean;
  salesAgreement?: SalesAgreement;
  reservationPrice?: number;
  registrationPlate: string;
  token: string;
  whenToSell: string;
}

export interface BusinessProposalResponseForId {
  baseObject?: BaseObject;
  isSigningContractRequired?: boolean;
  baseObjectId: string;
  registrationPlate: string;
  odometerReading: number;
  clientMemberId: string;
  reservationPrice?: number;
  debtAmount: number;
  hasDebt: boolean;
  manualValuationPending: boolean;
  hasManualValuation: boolean;
  deniedByKvd: boolean;
  denial?: Record<string, unknown>;
  salesFlow: SalesFlowType;
  auctionPriceInterval?: Interval;
  reservationPriceInterval?: Interval;
  tradeInPriceInterval?: Interval;
  salesAgreementSigned: boolean;
  isExpired: boolean;
  expirationDate: string;
  contactEmail: string;
  contactPhone: string;
  contactName: string;
  whenToSell: string;
  salesAgreement?: SalesAgreement;
}

export interface VehicleObject {
  auctionPriceInterval?: Interval;
  tradeInPriceInterval?: Interval;
  color?: string;
  expressPrice?: number;
  fabricAndMake?: string;
  fuelCodes?: FuelCodeType[];
  gearbox?: GearboxType;
  image?: {
    description: string;
    url: string;
  };
  ownerEmailAddress?: string;
  registrationPlate?: string;
  clientMemberEmail?: string;
  odometerReading?: number;
  requiresManualValuation?: boolean;
  salesFlow?: SalesFlowType;
  technicalSpecification?: TechnicalSpecification;
  ticketRole?: string;
  year?: string;
  submissionBooking?: {
    date?: TimeSlot;
    facility?: Facility;
    processObjectId: string;
  };
}

export interface SellFlow {
  vehicleObject?: VehicleObject;
  hydrated: boolean;
  clientMemberEmail: string;
  clientHasMemberAccount: boolean;
  emailForAccountRegistration: {
    email: string;
    accountType: string;
  };
}

interface BaseContactDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  campaignCode: string;
}

export interface DefaultContactDetails extends BaseContactDetails {
  whenToSell: SelectOption;
  whenToContact: SelectOption;
  consent: boolean;
  otherInformation?: string;
  extraTires?: boolean;
}

export interface ContactDetails extends BaseContactDetails {
  whenToSell: SelectOption['value'];
  whenToContact: SelectOption['value'];
  salesMethod: SalesMethodTypes | '';
  isTradeIn: boolean;
}

export interface ProposalObject extends CreateProposalInput {
  salesMethod: SalesMethodTypes;
}

export interface LeadObjectInformation extends VehicleObject {
  isTradeInAvailable: boolean;
  isTradeIn: boolean;
  initialTrafficDate: string;
  hasDiscountOpportunity: boolean;
}

export interface SalesAgreement {
  bankIdAutoStartToken: Nullable<string>;
  bankIdQrCode: string | undefined;
  bankIdOrderRef: string | undefined;
  baseObjectId: string;
  contactEmailAddress: string;
  createdAt: string;
  documentSigningUrl: string;
  id?: string;
  ownerEmailAddress: string;
  ownerMobilePhoneNumber?: string;
  provider: SalesAgreementProvider;
  returnUrl: string;
  signedAt: Nullable<string>;
  signingId: string;
  status: SigningInstanceStatus;
  updatedAt: Nullable<string>;
  contactIsOwner: boolean;
  contactNationalIdentificationNumber: string;
}

export type SelectedSpecItem = SpecItem & { value?: boolean };

export type SellFlowFormHeader =
  | Partial<SellFlowFormHeaderRecord>[]
  | undefined;

export interface SellFlow1 {
  defaultContactDetails: DefaultContactDetails;
  contactDetails: Partial<ContactDetails>;
  vehicleInfoLocked: boolean;
  odometerLocked: boolean;
  odometerReading: string;
  salesMethodClosed: boolean;
  valuationClosed: boolean;
  prefilledOdometerReadingInKilometers: string;
  registrationPlate: string;
  prefilledRegistrationPlate: string;
  trimOptionsLocked: boolean;
  isVehicleInfoLoading: boolean;
  odometerUnknown: boolean;
  isOdometerLoading: boolean;
  isTrimOptionsLoading: boolean;
  trimOptionsUnknown: boolean;
  leadObjectInformation: LeadObjectInformation | null;
  leadObjectInformationError: string;
  businessProposal: BusinessProposal | null;
  businessProposalLoading: boolean;
  businessProposalError: string;
  salesAgreementLoading: boolean;
  selectReservationPriceLoading: boolean;
  salesAgreement: SalesAgreement | null;
  salesAgreementError: string;
  ticketRole: TicketRole | null;
  token?: string;
  addonsSelected?: SelectedSpecItem[] | [];
  valuationStandardAddonsSelected: SelectedSpecItem[] | [];
  packageSelected?: string;
  isTrimOptionsEdited: boolean;
  salesMethod: SalesMethodTypes | '';
  packageUnknown: boolean | null;
  useErrorFallbackSolution: boolean;
}
